@let isMobile = isMobile$ | async;
@let readyToSubmitCount = readyToSubmitCount$ | async;
@let hasPendingFiles = hasPendingFiles$ | async;
@let hasInvalidInReviewItems = hasInvalidInReviewItems$ | async;
@let updatedSubmissionFilesCount = updatedSubmissionFilesCount$ | async;
@let showMobileSubmitButton = showMobileSubmitButton$ | async;

<mat-drawer-container class="client-request-panel-container" [hasBackdrop]="isMobile" (backdropClick)="closeDrawer()">
  <!-- Sidebar Drawer -->
  <mat-drawer
    #drawer
    [mode]="isMobile ? 'over' : 'side'"
    [opened]="!isMobile"
    class="client-request-navigation-sidebar"
    [dataSelector]="dataSelector + '-drawer'"
  >
    <div
      #sidebarScrollContent
      class="client-request-navigation-sidebar-content"
      [ngClass]="{ 'mobile-submit-btn-present': showMobileSubmitButton }"
      [dataSelector]="dataSelector + '-sidebar-content'"
    >
      <div class="close-buttons-container">
        @if (!isUsingSecureLink) {
          <button
            class="close-button"
            andButton
            color="ghost"
            size="small"
            [dataSelector]="dataSelector + '-close-button'"
            (click)="close()"
          >
            <aiwyn-icon [name]="(isMobile ?? false) ? 'arrow-left' : 'x'" [height]="20" [width]="20"></aiwyn-icon>
          </button>
        }

        @if (isMobile) {
          <button
            class="drawer-close-button"
            andButton
            color="ghost"
            size="small"
            [dataSelector]="dataSelector + '-close-button-mobile'"
            (click)="closeDrawer()"
          >
            <aiwyn-icon name="x" [height]="20" [width]="20"></aiwyn-icon>
          </button>
        }
      </div>

      <div
        class="client-request-navigation-sidebar-header"
        [ngClass]="(isMobile ?? false) ? 'align-items-center' : ''"
        [dataSelector]="dataSelector + '-header'"
      >
        <h1 class="h1-600 color-gray-100" [dataSelector]="dataSelector + '-request-name'">{{ requestName$ | async }}</h1>

        <div class="d-flex flex-wrap gap-2" [dataSelector]="dataSelector + '-client-group-info'">
          <ng-container *ngTemplateOutlet="clientsTemplate"></ng-container>
          <p class="body-400 color-gray-100" [dataSelector]="dataSelector + '-group-name'">{{ projectName$ | async }}</p>
        </div>

        <ng-container *ngTemplateOutlet="dueDateAndAssigneesTemplate"></ng-container>
      </div>

      @if (rejectedRequests$ | async; as rejectedRequests) {
        @if (rejectedRequests.length) {
          <div class="rejected-items-block" [dataSelector]="dataSelector + '-rejected-items-block'">
            <p [dataSelector]="dataSelector + '-rejected-items-message'">
              {{ rejectedRequests.length === 1 ? 'One' : 'Some' }} of your request items require resubmission:
            </p>

            <ul class="rejected-items-list" [dataSelector]="dataSelector + '-rejected-items-list'">
              @for (rejectedRequest of rejectedRequests; track rejectedRequest.id; let index = $index) {
                <li [dataSelector]="dataSelector + '-rejected-item-' + index">{{ rejectedRequest.name }}</li>
              }
            </ul>
          </div>
        }
      }

      @if (isReadonly$ | async) {
        <div class="completed-block body-300">
          This request has been submitted for review.<br /><br />
          If you need to make any changes to the submitted files please reach out to our
          <a type="email" [href]="'mailto:' + (instance.staff$ | async)?.data?.email">team</a>.
        </div>
      } @else {
        <app-text-toggle [text]="(requestDescription$ | async) ?? ''" [maxCharacters]="150" [enableHtml]="true" class="px-6 py-4" textClass="body-300 color-gray-400 ">
        </app-text-toggle>

        @if (!isMobile) {
          <p class="body-300 color-gray-400 px-6 pt-1 pb-4">Your progress will be saved if you need to come back and finish later.</p>
        }
      }

      @if (!isLoading) {
        <div class="totals-tile-container px-6" [dataSelector]="dataSelector + '-items-summary'">
          @let isClientRequestsUpdatedFiltersEnabled = isClientRequestsUpdatedFiltersEnabled$ | async;
          @for (item of selectedRequestItemStatusFilterDetails$ | async; track item.type) {
            <button
              class="totals-tile color-gray-100"
              [ngClass]="{
                'totals-tile__active': isClientRequestsUpdatedFiltersEnabled && item.active,
                'cursor-default': !isClientRequestsUpdatedFiltersEnabled,
              }"
              [dataSelector]="dataSelector + '-items-summary-tile-' + item.color"
              [attr.aria-description]="
                'Total number of ' + item.label + ' items.' + isClientRequestsUpdatedFiltersEnabled ? ' Click to toggle filter.' : ''
              "
              (click)="toggleItemFilter(!item.active, 'status', item.type)"
            >
              <span class="totals-tile-label">
                <awn-badge class="me-1" [color]="item.color"></awn-badge>
                {{ item.label }}
              </span>
              <span class="totals-tile-total">{{ item.total }}</span>
            </button>
          }
        </div>
      }

      <mat-tab-group
        #tabGroup
        mat-align-tabs="start"
        [disableRipple]="true"
        [fitInkBarToContent]="true"
        [mat-stretch-tabs]="false"
        class="tab-bar pt-4 px-2 flex-grow-1 overflow-hidden"
        [dataSelector]="dataSelector + '-tab-group'"
      >
        <mat-tab label="Requests" [dataSelector]="dataSelector + '-requests-tab'">
          <div class="tab-content-scrollable">
            <div class="p-4">
              <awn-client-request-navigation-tab
                [instance]="instance"
                [dataSelector]="dataSelector + '-navigation-tab'"
              ></awn-client-request-navigation-tab>
            </div>
          </div>
        </mat-tab>

        <mat-tab label="Files" [dataSelector]="dataSelector + '-files-tab'">
          <div class="tab-content-scrollable">
            <div class="p-4">
              <awn-client-request-document-upload-tab
                [instance]="instance"
                [dataSelector]="dataSelector + '-document-upload-tab'"
              ></awn-client-request-document-upload-tab>
            </div>
          </div>
        </mat-tab>

        @if (enableReferenceFiles$ | async) {
          <mat-tab label="Reference Files" [dataSelector]="dataSelector + '-reference-files-tab'">
            <div class="tab-content-scrollable">
              <div class="reference-files-list" [dataSelector]="dataSelector + '-reference-files-list'">
                @for (fileRef of allReferenceFiles$ | async; track fileRef.id; let last = $last) {
                  <div class="reference-file-item" [dataSelector]="dataSelector + '-reference-file-' + fileRef.id">
                    <div class="reference-file-info">
                      <button
                        class="reference-file-name-button"
                        (click)="previewReferenceFile(fileRef)"
                        [dataSelector]="dataSelector + '-reference-file-name-' + fileRef.id"
                      >
                        {{ fileRef.file.name }}
                      </button>
                      <span class="reference-file-size body-300 color-gray-400">
                        {{ fileRef.file.fileInfo?.size ? (fileRef.file.fileInfo.size | fileSize) : 'File size unavailable' }}
                      </span>
                    </div>
                    <button
                      andButton
                      color="ghost"
                      size="small"
                      class="reference-file-download"
                      (click)="downloadReferenceFile(fileRef)"
                      [dataSelector]="dataSelector + '-reference-file-download-' + fileRef.id"
                    >
                      <aiwyn-icon name="download" [height]="20" [width]="20"></aiwyn-icon>
                    </button>
                  </div>
                  @if (!last) {
                    <hr class="reference-file-separator" />
                  }
                } @empty {
                  <div class="reference-files-empty" [dataSelector]="dataSelector + '-reference-files-empty'">
                    <aiwyn-icon name="file-text" [height]="24" [width]="24" class="color-gray-400"></aiwyn-icon>
                    <span class="body-400 color-gray-400">No reference files have been shared yet.</span>
                  </div>
                }
              </div>
            </div>
          </mat-tab>
        }
      </mat-tab-group>
    </div>

    <!-- Desktop: Footer stays in drawer -->
    @if (!isMobile) {
      <div class="drawer-footer-wrapper">
        <ng-container *ngTemplateOutlet="footerTemplate"></ng-container>
      </div>
    }
  </mat-drawer>

  <!-- Main Content Area -->
  <mat-drawer-content
    class="client-request-content"
    [ngClass]="{ 'mobile-content': isMobile, 'mobile-submit-btn-present': showMobileSubmitButton }"
    [dataSelector]="dataSelector + '-main-content'"
  >
    @if (isLoading) {
      <div class="d-flex justify-content-center align-items-center" style="height: 100%">
        <mat-spinner></mat-spinner>
      </div>
    } @else {
      <!-- Mobile View -->
      @if (isMobile) {
        <!-- Sticky header wrapper -->
        <div class="sticky-header-wrapper">
          <!-- Mobile: First row with hamburger + title + badges -->
          <ng-container *ngTemplateOutlet="mobileHeaderBlock"></ng-container>
          <ng-container *ngTemplateOutlet="searchBarBlock"></ng-container>
          <ng-container *ngTemplateOutlet="mobileUploadFiles"></ng-container>
        </div>

        <div class="client-request-content-scrollable" [dataSelector]="dataSelector + '-content-scrollable'">
          <awn-flagged-files-summary [isMobileMode]="isMobile"></awn-flagged-files-summary>
          <ng-container *ngTemplateOutlet="requestTotalLabel"></ng-container>
          <ng-container *ngTemplateOutlet="sectionsListBlock"></ng-container>
          <ng-container *ngTemplateOutlet="uncategorizedFilesSectionBlock"></ng-container>
        </div>
      } @else {
        <!-- Desktop View -->
        <awn-flagged-files-summary></awn-flagged-files-summary>

        <!-- Search bar row -->
        <ng-container *ngTemplateOutlet="searchBarBlock"></ng-container>
        <ng-container *ngTemplateOutlet="requestTotalLabel"></ng-container>

        <div class="client-request-content-scrollable" [dataSelector]="dataSelector + '-content-scrollable'">
          <ng-container *ngTemplateOutlet="sectionsListBlock"></ng-container>
          <ng-container *ngTemplateOutlet="uncategorizedFilesSectionBlock"></ng-container>
        </div>
      }
    }
    <!-- Loading END -->
  </mat-drawer-content>
</mat-drawer-container>

<!-- Mobile: Sticky footer at bottom -->
@if (showMobileSubmitButton) {
  <div class="mobile-sticky-footer" [dataSelector]="dataSelector + '-mobile-footer'">
    <ng-container *ngTemplateOutlet="footerTemplate"></ng-container>
  </div>
}

<app-sidebar
  [headerTitle]="'Activity'"
  [collapsible]="!isMobile"
  [isOpen]="showActivitySidebar()"
  [dataSelector]="dataSelector + '-activity-sidebar'"
  [ngClass]="enableCommentsInActivitySidebar() ? 'sidebar-comments' : ''"
  [showUnseenDot]="(haveUnreadComments() ?? false)"
  [showMobileActions]="(isMobile ?? false)"
  (toggle)="toggleActivitySidebar()"
  (goBackIconClicked)="handleGoBack()"
  (showAllClicked)="handleShowAll()"
>
  @if (enableCommentsInActivitySidebar()) {
    <!-- NEW: Comments Section -->
    <app-comments-section
      [entityIds]="commentSectionIds()"
      [isActivityLoading]="(isActivityLoading() ?? false)"
      [dataSelector]="dataSelector + '-comments-section'"
    />
  } @else {
    <!-- ORIGINAL: Activity Content (preserved for when flag is off) -->
    @for (answerActivity of answerActivity$ | async; track $index) {
      <awn-activity-item [answerActivity]="answerActivity" [dataSelector]="dataSelector + '-activity-item-' + $index"></awn-activity-item>
    } @empty {
      @if (isActivityLoading()) {
        <div class="activity-loading-panel" [dataSelector]="dataSelector + '-activity-loading'">
          <mat-spinner diameter="24"></mat-spinner>
          <span [dataSelector]="dataSelector + '-activity-loading-message'">Loading activities...</span>
        </div>
      } @else {
        <div class="activity-empty-panel" [dataSelector]="dataSelector + '-activity-empty'">
          <span [dataSelector]="dataSelector + '-activity-empty-message'">No activity to display</span>
        </div>
      }
    }
  }
</app-sidebar>

<!-- ======= TEMPLATES BLOCK ========  -->

<!-- Footer Template (reused for desktop drawer and mobile sticky) -->
<ng-template #footerTemplate>
  @if (isReadonly$ | async) {
    <div
      class="d-flex p-6 gap-4"
      [ngClass]="{ 'indicate-scrollable-content-above': showFooterBoxShadow$ | async }"
      [dataSelector]="dataSelector + '-readonly-footer'"
    >
      <button andButton color="default" size="large" class="w-100" [dataSelector]="dataSelector + '-back-button'" (click)="close()">
        Back
      </button>
    </div>
  } @else {
    <div
      class="d-flex flex-column p-6"
      [class]="{
        'indicate-scrollable-content-above': showFooterBoxShadow$ | async,
        'success-submit-section': submittedCount() > 0 && readyToSubmitCount === 0 && updatedSubmissionFilesCount === 0,
      }"
    >
      @if ((readyToSubmitCount ?? 0) > 0) {
        <div class="d-flex gap-4" [dataSelector]="dataSelector + '-action-buttons'">
          <div
            class="flex-fill-container-x flex-grow-1"
            [tooltip]="
              hasInvalidInReviewItems
                ? 'Cannot submit while there are empty document request items'
                : isSubmissionButtonLoading()
                  ? 'Waiting for uploading/categorizing files to complete.'
                  : 'Wait for files to upload and categorize before saving'
            "
            [tooltipEnabled]="(hasInvalidInReviewItems ?? false) || (isSubmissionButtonLoading() ?? false) || (hasPendingFiles ?? false)"
          >
            <button
              andButton
              color="primary"
              size="large"
              class="w-100"
              [isLoading]="isSubmissionButtonLoading()"
              [disabled]="(hasPendingFiles ?? false) || (hasInvalidInReviewItems ?? false)"
              [dataSelector]="dataSelector + '-submit-all-button'"
              (click)="submit()"
            >
              Submit {{ readyToSubmitCount }} {{ readyToSubmitCount > 1 ? 'Requests' : 'Request' }} for review
            </button>
          </div>
        </div>
      } @else if ((updatedSubmissionFilesCount ?? 0) > 0) {
        <div class="d-flex gap-4" [dataSelector]="dataSelector + '-action-buttons'">
          <div
            class="flex-fill-container-x flex-grow-1"
            [tooltip]="
              isSubmissionButtonLoading()
                ? 'Waiting for uploading/categorizing files to complete.'
                : 'Wait for files to upload and categorize before saving'
            "
            [tooltipEnabled]="(isSubmissionButtonLoading() ?? false) || (hasPendingFiles ?? false)"
          >
            <button
              andButton
              color="primary"
              size="large"
              class="w-100"
              [isLoading]="isSubmissionButtonLoading()"
              [disabled]="(hasPendingFiles ?? false)"
              [dataSelector]="dataSelector + '-submit-files-button'"
              (click)="submit()"
            >
              Submit {{ updatedSubmissionFilesCount }} {{ updatedSubmissionFilesCount > 1 ? 'files' : 'file' }} for review
            </button>
          </div>
        </div>
      } @else if (submittedCount() > 0) {
        <h3 class="h3-500 color-green-400">
          <aiwyn-icon name="check-circle" [height]="14" [width]="14"></aiwyn-icon>
          You've submitted {{ submittedCount() }} requests for review.
        </h3>

        <span class="body-500 color-gray-300">You can respond to other requests or update previous responses anytime.</span>
      } @else {
        <span class="text-center body-600 color-gray-300" [dataSelector]="dataSelector + '-progress-hint'"
          >Nothing to submit yet — respond to at least one request to continue.</span
        >
      }
    </div>
  }
</ng-template>

<ng-template #clientsTemplate>
  <div *ngIf="clientName$ | async as clientName" class="d-flex align-items-center">
    <aiwyn-icon name="user" class="me-1" [height]="14" [width]="14"></aiwyn-icon>

    <label>{{ clientName }}</label>
  </div>
</ng-template>

<ng-template #dueDateAndAssigneesTemplate>
  <div class="summary-row">
    <div>
      <aiwyn-icon name="user" [ngClass]="'icon-color-gray-400'" [height]="14" [width]="14"></aiwyn-icon>
      Client Owner
    </div>
    <div class="summary-details">
      <app-avatar [avatarInput]="(instance.avatars$ | async) ?? []" [domain]="'contact'" [limit]="3" [showLabel]="true" nameStyle="full" class="ms-2"></app-avatar>
    </div>
  </div>
  <div class="summary-row">
    <div>
      <aiwyn-icon name="calendar" [ngClass]="'icon-color-gray-400'" [height]="14" [width]="14"></aiwyn-icon>
      Next Due
    </div>
    <div class="summary-details">
      <p class="body-300 color-gray-100 text-wrap">{{ instance.nextDueDate$ | async | date: 'EEEE, MMMM d' }}</p>
      <p class="small-300 color-gray-400 text-wrap">{{ instance.nextDocumentDue$ | async }}</p>
    </div>
  </div>
</ng-template>

<ng-template #hideNotApplicableTemplate>
  @if (enableMarkNotApplicable$ | async) {
    <div class="d-flex align-items-center flex-nowrap gap-1">
      <mat-slide-toggle
        [checked]="hideNotApplicable()"
        (change)="toggleHideNotApplicable($event)"
        class="filter-toggle"
        aria-label="Hide marked as not applicable requests"
        [dataSelector]="dataSelector + '-hide-not-applicable-toggle'"
      ></mat-slide-toggle>
      <span class="filter-toggle-label body-400 color-gray-200 text-nowrap">Hide marked as N/A</span>
    </div>
  }
</ng-template>

<ng-template #mobileUploadFiles>
  @if (!(isReadonly$ | async)) {
    <div class="mobile-file-upload-section" [dataSelector]="dataSelector + '-mobile-upload'">
      <awn-upload-files
        type="button"
        [accept]="acceptedFileTypes"
        [entityId]="(jobId$ | async) ?? 0"
        (onFileUploadStart)="onUploadFilesStart($event)"
        (onFileUploadEnd)="onUploadFilesComplete($event, false)"
        [dataSelector]="dataSelector + '-upload-component-mobile'"
      ></awn-upload-files>
    </div>
  }
</ng-template>

<ng-template #requestTotalLabel>
  <div class="request-counter body-300 color-gray-400 d-flex align-items-center justify-content-between">
    Showing {{ visibleRequestCount$ | async }} of {{ totalRequestCount$ | async }} requests

    @if (isClientRequestsUpdatedFiltersEnabled$ | async) {
      <ng-container *ngTemplateOutlet="hideNotApplicableTemplate"></ng-container>
    }
  </div>
</ng-template>

<ng-template #searchBarBlock>
  <div class="filter-bar d-flex justify-content-between align-items-center" [dataSelector]="dataSelector + '-filter-bar'">
    <input
      type="text"
      role="searchbox"
      placeholder="Search requests..."
      class="form-input form-input--search"
      [formControl]="searchControl"
      [dataSelector]="dataSelector + '-search-input'"
    />

    @if (isClientRequestsUpdatedFiltersEnabled$ | async) {
      <button
        andButton
        [matMenuTriggerFor]="itemFilterMenu"
        class="ms-4 no-dropdown-arrow"
        [dataSelector]="dataSelector + '-filter-button'"
      >
        <aiwyn-icon name="funnel-simple" class="me-2 no-stroke-icon icon-color-gray-600" />
        Filter
      </button>

      <mat-menu #itemFilterMenu="matMenu">
        <div class="d-flex flex-column px-4" (click)="$event.stopPropagation()">
          <label class="group-500 py-1 color-gray-400">STATUS</label>
          @for (statusFilterItem of selectedRequestItemStatusFilterDetails$ | async; track statusFilterItem.type) {
            <mat-checkbox
              [checked]="statusFilterItem.active"
              [disabled]="statusFilterItem.disabled"
              class="mat-checkbox-no-label-margin d-flex"
              [style.padding]="'6px 0'"
              (change)="toggleItemFilter($event.checked, 'status', statusFilterItem.type)"
            >
              <pill [color]="statusFilterItem.color" [style.opacity]="statusFilterItem.disabled ? 0.8 : 1">
                <ng-container ngProjectAs="pill-content">
                  <awn-badge class="me-1" [color]="statusFilterItem.color" />
                  {{ statusFilterItem.label }}
                </ng-container>
              </pill>
            </mat-checkbox>
          }

          <label class="group-500 py-1 color-gray-400">DUE DATE</label>
          @for (dueDateFilterItem of dueDateFilterOptions$ | async; track dueDateFilterItem.value) {
            <mat-checkbox
              [checked]="dueDateFilterItem.active"
              [disabled]="dueDateFilterItem.disabled"
              class="color-gray-100 d-flex"
              [style.padding]="'6px 0'"
              (change)="toggleItemFilter($event.checked, 'dueDate', dueDateFilterItem.value)"
            >
              {{ dueDateFilterItem.label }}
            </mat-checkbox>
          }
        </div>

        <div class="d-flex w-100 border-top-1 border-color-gray-800 pt-1 px-4" (click)="$event.stopPropagation()">
          <button andButton color="ghost-secondary" class="w-100" (click)="clearFilters()" [disabled]="!doesHaveActiveItemFilters()">
            Clear filters
          </button>
        </div>
      </mat-menu>
    } @else {
      <ng-container *ngTemplateOutlet="hideNotApplicableTemplate"></ng-container>
    }
  </div>

  @if ((isClientRequestsUpdatedFiltersEnabled$ | async) && doesHaveActiveItemFilters()) {
    <div class="d-flex align-items-center gap-2 mb-6" [dataSelector]="dataSelector + '-active-filters-bar'">
      @for (activeFilterChip of activeFilterChips$ | async; track activeFilterChip.value) {
        <div class="active-filter-chip d-flex align-items-center gap-2">
          {{ activeFilterChip.type }}: {{ activeFilterChip.value }}
          <aiwyn-icon
            name="x"
            class="cursor-pointer"
            tabindex="0"
            [height]="16"
            [width]="16"
            (click)="toggleItemFilter(false, activeFilterChip.path[0], activeFilterChip.path[1])"
            [dataSelector]="dataSelector + '-active-filter-chip-remove-' + activeFilterChip.type + '-' + activeFilterChip.value"
          />
        </div>
      }

      <button andButton color="ghost" size="small" (click)="clearFilters()" [dataSelector]="dataSelector + '-clear-filter-chips-button'">
        Clear filters
      </button>
    </div>
  }
</ng-template>

<ng-template #mobileHeaderBlock>
  <div class="mobile-header-row d-flex align-items-center justify-content-between" [dataSelector]="dataSelector + '-mobile-header'">
    <div class="d-flex align-items-center gap-3 flex-grow-1">
      <button
        class="mobile-hamburger-icon-button"
        andButton
        color="ghost"
        size="small"
        [dataSelector]="dataSelector + '-hamburger-button'"
        (click)="toggleDrawer()"
        aria-label="Open navigation menu"
      >
        <aiwyn-icon name="list" [height]="24" [width]="24"></aiwyn-icon>
      </button>

      <h2 class="h2-600 color-gray-100 mb-0" [dataSelector]="dataSelector + '-mobile-title'">
        {{ (requestName$ | async) ?? '' }}
      </h2>
    </div>
  </div>
</ng-template>

<ng-template #sectionsListBlock>
  @for (section of (filteredSections$ | async) ?? []; track section.id; let $sectionIndex = $index) {
    <awn-request-section [section]="section" [dataSelector]="dataSelector + '-section-' + $sectionIndex">
      @for (requestItem of section.items; track requestItem.id; let $requestItemIndex = $index) {
        <awn-request-item-card
          [requestItem]="requestItem"
          [sectionReferenceId]="section.referenceId"
          [instance]="instance"
          [readonly]="((isReadonly$ | async) ?? false) || isRequestItemNotApplicable(requestItem.id)"
          [dataSelector]="dataSelector + '-request-item-' + $sectionIndex + '-' + $requestItemIndex"
          (showRequestActivity)="toggleActivitySidebar(true)"
        >
          <ng-container [ngSwitch]="requestItem.type">
            <ng-container *ngSwitchCase="RequestItemType.DOCUMENT_REQUEST">
              <awn-document-item-content
                [readonly]="((isReadonly$ | async) ?? false) || isRequestItemNotApplicable(requestItem.id)"
                [documentItem]="$any(requestItem)"
                [instance]="instance"
                [dataSelector]="dataSelector + '-document-item-content-' + $sectionIndex + '-' + $requestItemIndex"
              ></awn-document-item-content>
            </ng-container>

            <ng-container *ngSwitchCase="RequestItemType.QUESTIONNAIRE">
              <awn-questionnaire-item-content
                [readonly]="(isReadonly$ | async) ?? false"
                [notApplicable]="isRequestItemNotApplicable(requestItem.id)"
                [questionnaireItem]="$any(requestItem)"
                [documentIndex]="$requestItemIndex"
                [sectionIndex]="$sectionIndex"
                [dataSelector]="dataSelector + '-questionnaire-item-' + $sectionIndex + '-' + $requestItemIndex"
              ></awn-questionnaire-item-content>
            </ng-container>

            <ng-container *ngSwitchDefault> Unsupported request type, please reach out support team for help </ng-container>
          </ng-container>

          @if ((enableMarkNotApplicable$ | async) ?? false) {
            <awn-not-applicable-content
              [hasUploadedFiles]="requestItem | hasUploadedFiles"
              [requestItemStatus]="requestItem.status"
              [isNotApplicable]="isRequestItemNotApplicable(requestItem.id)"
              [isItemRestricted]="isRequestItemRestricted(requestItem)"
              [requestItemId]="requestItem.id"
              [requestItemName]="requestItem.name"
              [existingNote]="getExistingNote(requestItem.id)"
              [dataSelector]="dataSelector + '-not-applicable-' + $sectionIndex + '-' + $requestItemIndex"
              (notApplicableChange)="onNotApplicableChange(requestItem.id, $event)"
            ></awn-not-applicable-content>
          }
        </awn-request-item-card>
      }
    </awn-request-section>
  } @empty {
    <div class="request-list-empty-panel">
      <aiwyn-icon name="search" [height]="24" [width]="24"></aiwyn-icon>

      <h2 class="h2-500">No results found</h2>

      <span> Try adjusting your search or filters to find what you’re looking for. </span>
    </div>
  }
</ng-template>

<ng-template #uncategorizedFilesSectionBlock>
  @if ((showUncategorizedFilesSection$ | async) ?? false) {
    <div class="mt-6">
      <h2 class="h2-500 my-3">Other Files</h2>

      <hr class="other-files-hr" />

      <p class="body-300 color-gray-400 mt-6 mb-4">
        Any files that aren’t categorized to specific requests will show up in this section. You can also upload anything else you’d like to
        share.
      </p>

      <awn-upload-files
        [accept]="acceptedFileTypes"
        [entityId]="(jobId$ | async) ?? 0"
        (onFileUploadStart)="onUploadFilesStart($event)"
        (onFileUploadEnd)="onUploadFilesComplete($event)"
        [dataSelector]="dataSelector + '-upload-component-others-section'"
      ></awn-upload-files>
    </div>

    <div class="file-list">
      @for (fileUploadJobId of stagedFilesForThisDocumentIds(); track fileUploadJobId) {
        <awn-file-upload-job-item class="file-item" [jobId]="fileUploadJobId" />
      }
      @for (file of uncategorizedFileDisplays(); track file.id; let i = $index) {
        <awn-file-item
          class="file-item"
          [file]="file"
          [fileIndex]="i"
          [siblingFiles]="uncategorizedFileDisplays()"
          [isEditingAllowed]="!((isReadonly$ | async) ?? false)"
          [instance]="instance"
        ></awn-file-item>
      }
    </div>
  }
</ng-template>
